import MapGL, { Layer, Marker, Popup, Source } from "react-map-gl/mapbox"
import "mapbox-gl/dist/mapbox-gl.css"
import type { MapboxGeoJSONFeature, MapLayerMouseEvent } from "mapbox-gl"
import { useMemo, useCallback, memo } from "react"
import { center as turfCenter } from "@turf/turf"
import type { MapRef } from "react-map-gl/mapbox"

import { CadastreGouvLayer } from "./layers/cadastre-gouv-layer"
import { CommuneContourLayer } from "./layers/commune-contour-layer"
import { GeoportailWFSLayer } from "./layers/geoportail-wfs-layer"
import { PCSLayer } from "./layers/pcs-layer"
import { DPELayer } from "./layers/dpe-layer"
import { CommunePluLayer } from "./layers/commune-plu-layer"
import { ABFLayer } from "./layers/abf-layer"
import { StationPerimeterLayer } from "./layers/station-perimeter-layer"
import { QPVLayer } from "./layers/qpv-layer"
import { ArgileLayer } from "./layers/argile-layer"
import { EarthquakeLayer } from "./layers/earthquake-layer"
import { RadonLayer } from "./layers/radon-layer"
import { FloodingLayer } from "./layers/flooding-layer"
import { PPRILayer } from "./layers/ppri-layer"
import { PollutionLayer } from "./layers/pollution-layer"
import { ThreeDLayer } from "./layers/three-d-layer"
import { TerrainLayer } from "./layers/terrain-layer"
import { IsochroneLayer } from "./layers/isochrone-layer"
import { HeatNetworkLayer } from "./layers/heat-network-layer"
import { SelectedLayer } from "./layers/selected-layer"
import { SearchParcelsLayer } from "./layers/search-parcels-layer"
import { ProjectsLayer } from "./layers/projects-layer"

import { StudyMapContent } from "./panels/study-map-content"
import { PCPopup } from "./panels/pc-popup"
import { DPEPopup } from "./panels/dpe-popup"
import { ProjectPopup } from "./panels/project-popup"
import type { ProjectPopupData } from "./panels/project-popup"

import { MeasureDistance } from "./tools/measure-distance"
import { MeasureSurface } from "./tools/measure-surface"
import { Altimetry } from "./tools/altimetry"

import type { Feature } from "geojson"
import type { ICommune, IParcel, IPermit, IDPE, POIMarker, MutationMarker } from "@/types/explore"
import type { UseConstructionStudyReturn } from "@/hooks/explore/use-construction-study"

const MAPBOX_SATELLITE_STYLE = "kaptcher/cltell1nn01dm01piblgud9d2"
const MAPBOX_STREET_STYLE = "kaptcher/cm1umwib0015k01qr8tra14o9"

type PrescriptionPopup = {
  longitude: number
  latitude: number
  libelle: string
  txt: string
}

interface ExploreMapProps {
  mapRef: React.RefObject<MapRef | null>
  mapboxToken: string
  style: string
  selectedCommune: ICommune | undefined
  selectedParcels: IParcel[]
  selectedPLU: MapboxGeoJSONFeature | null
  isStudyMode: boolean
  isToolOn: boolean
  activeTool: string | null
  activeLayers: string[]
  parcelPOI: { lon: number | null; lat: number | null }
  selectedPC: { permit: IPermit; longitude: number; latitude: number } | null
  setSelectedPC: (pc: { permit: IPermit; longitude: number; latitude: number } | null) => void
  selectedDPE?: { dpe: IDPE; longitude: number; latitude: number } | null
  setSelectedDPE?: (dpe: { dpe: IDPE; longitude: number; latitude: number } | null) => void
  selectedProject?: { project: ProjectPopupData; longitude: number; latitude: number } | null
  setSelectedProject?: (p: { project: ProjectPopupData; longitude: number; latitude: number } | null) => void
  prescriptionPopup: PrescriptionPopup | null
  setPrescriptionPopup: (popup: PrescriptionPopup | null) => void
  handleClick: (e: MapLayerMouseEvent) => void
  onMouseHandler: (e: MapLayerMouseEvent) => void
  onMouseLeave: (e: MapLayerMouseEvent) => void
  center: number[]
  onMapIdle: () => void
  onMoveStart: () => void
  onMoveEnd?: () => void
  isCommuneCovered: boolean
  layerFilters?: Record<string, Record<string, unknown>>
  pluData?: Feature[]
  pluTypeZones?: string[]
  pluOpacity?: number
  pluLibelles?: string[]
  parcelFilter?: {
    minParcelSurface: number
    maxParcelSurface: number
    ownerTypes: string[]
  }
  isParcelFilterOn?: boolean
  onParcelFilterDataLoaded?: (features: IParcel[]) => void
  activePOIMarkers?: POIMarker[]
  activeMutationMarkers?: MutationMarker[]
  study?: UseConstructionStudyReturn
}

const CATEGORY_MARKER_COLORS: Record<string, string> = {
  Transport: "#3b82f6",
  Commerce: "#f97316",
  Sante: "#ef4444",
  Education: "#22c55e",
  Monuments: "#8b5cf6",
  Autres: "#6b7280",
}

const getMapStyle = (style: string) => {
  switch (style) {
    case "cadastre":
    case "mono":
      return "mapbox://styles/mapbox/light-v11"
    case "satellite":
      return `mapbox://styles/${MAPBOX_SATELLITE_STYLE}`
    default:
      return `mapbox://styles/${MAPBOX_STREET_STYLE}`
  }
}

export const ExploreMap = memo((props: ExploreMapProps) => {
  const {
    mapRef,
    mapboxToken,
    style,
    selectedCommune,
    selectedParcels,
    activeTool,
    activeLayers,
    parcelPOI,
    selectedPC,
    setSelectedPC,
    selectedDPE,
    setSelectedDPE,
    selectedProject,
    setSelectedProject,
    prescriptionPopup,
    setPrescriptionPopup,
    handleClick,
    onMouseHandler,
    onMouseLeave,
    center,
    onMapIdle,
    onMoveStart,
    onMoveEnd,
    isCommuneCovered,
    layerFilters,
    pluData,
    pluTypeZones,
    pluOpacity,
    pluLibelles,
    parcelFilter,
    isParcelFilterOn,
    onParcelFilterDataLoaded,
    activePOIMarkers,
    activeMutationMarkers,
    study,
  } = props

  const projectLayerIds = ["prospects-dossiers-fill", "prospects-dossiers-point", "patrimoine-fill", "patrimoine-point"]

  const guardedHandleClick = useCallback(
    (e: MapLayerMouseEvent) => {
      if (!isCommuneCovered) return
      setSelectedDPE?.(null)

      const projectFeature = e?.features?.find((f) => f.layer && projectLayerIds.includes(f.layer.id))
      if (projectFeature) {
        const props = projectFeature.properties
        // GeoJSON properties flatten nested objects to JSON strings
        let pipeStatus = props?.pipe_status ?? null
        if (typeof pipeStatus === "string") {
          try { pipeStatus = JSON.parse(pipeStatus) } catch { pipeStatus = null }
        }
        setSelectedProject?.({
          project: {
            id: props?.id,
            name: props?.name,
            status: props?.status,
            pipe_status: pipeStatus,
            address: props?.address,
            property_type: props?.property_type,
            house_surface: props?.house_surface,
            field_surface: props?.field_surface,
          },
          longitude: e.lngLat.lng,
          latitude: e.lngLat.lat,
        })
      } else {
        setSelectedProject?.(null)
      }

      handleClick(e)
    },
    [isCommuneCovered, handleClick, setSelectedDPE, setSelectedProject],
  )

  const isochroneCenter = useMemo<[number, number] | null>(() => {
    const parcel = selectedParcels?.[0]
    if (parcel?.center) return parcel.center as [number, number]
    if (parcel?.geometry) return turfCenter(parcel.geometry).geometry.coordinates as [number, number]
    const coords = selectedCommune?.centre?.coordinates
    if (coords) return coords as [number, number]
    return null
  }, [selectedParcels, selectedCommune])

  const interactiveLayerIds = useMemo(() => {
    const ids: string[] = ["cadastre-layer"]
    if (activeLayers.includes("pc")) ids.push("pcs")
    if (activeLayers.includes("prospects-dossiers")) ids.push("prospects-dossiers-fill", "prospects-dossiers-point")
    if (activeLayers.includes("patrimoine")) ids.push("patrimoine-fill", "patrimoine-point")
    if (activeLayers.some((l) => l.startsWith("presc-") || l.startsWith("info-")))
      ids.push("wfs-layer", "wfs-layer-border", "wfs-layer-pct", "wfs-layer-line")
    if (activeLayers.includes("plu") || activeLayers.includes("commune-plu")) ids.push("commune-plu")
    if (isParcelFilterOn) ids.push("search-parcels")
    return ids
  }, [activeLayers, isParcelFilterOn])

  const poiMarkerElements = useMemo(
    () =>
      activePOIMarkers?.map((poi) => (
        <Marker key={`poi-${poi.id}`} longitude={poi.lon} latitude={poi.lat} anchor="center">
          <div
            className="w-3 h-3 rounded-full border-2 border-white shadow"
            style={{ backgroundColor: CATEGORY_MARKER_COLORS[poi.category] || CATEGORY_MARKER_COLORS.Autres }}
          />
        </Marker>
      )),
    [activePOIMarkers],
  )

  const mutationMarkerElements = useMemo(
    () =>
      activeMutationMarkers?.map((m, i) => (
        <Marker key={`mut-${i}`} longitude={m.lon} latitude={m.lat} anchor="bottom">
          <div className="bg-primary text-white text-[10px] font-bold px-1.5 py-0.5 rounded shadow whitespace-nowrap">
            {m.label}
          </div>
        </Marker>
      )),
    [activeMutationMarkers],
  )

  const longitude = selectedCommune?.centre?.coordinates?.[0] ?? center?.[0]
  const latitude = selectedCommune?.centre?.coordinates?.[1] ?? center?.[1]

  if (longitude == null || latitude == null) return null

  return (
    <MapGL
      ref={mapRef}
      initialViewState={{ longitude, latitude, zoom: 8 }}
      mapStyle={getMapStyle(style)}
      mapboxAccessToken={mapboxToken}
      style={{ height: "100%", width: "100%", cursor: !isCommuneCovered ? "default" : undefined }}
      interactiveLayerIds={interactiveLayerIds}
      onMoveStart={onMoveStart}
      onMoveEnd={onMoveEnd}
      onIdle={onMapIdle}
      onClick={guardedHandleClick}
      onMouseMove={onMouseHandler}
      onMouseLeave={onMouseLeave}
    >
      {study && <StudyMapContent study={study} />}

      {activeLayers.includes("prospects-dossiers") && (
        <ProjectsLayer commune={selectedCommune} statuses={["pending"]} color="#8b5cf6" sourceId="prospects-dossiers" />
      )}
      {activeLayers.includes("patrimoine") && (
        <ProjectsLayer commune={selectedCommune} statuses={["asset"]} color="#22c55e" sourceId="patrimoine" />
      )}

      {(style === "cadastre" || activeLayers.includes("cadastre")) && (
        <CadastreGouvLayer
          opacity={
            layerFilters?.cadastre?.["cad-opacity"] != null
              ? (layerFilters.cadastre["cad-opacity"] as number) / 100
              : 0.8
          }
          cityCode={selectedCommune?.code ?? ""}
        />
      )}

      {selectedCommune && <CommuneContourLayer commune={selectedCommune} />}

      {activeLayers.some((l) => l.startsWith("presc-") || l.startsWith("info-")) && (
        <GeoportailWFSLayer activeLayers={activeLayers} commune={selectedCommune} />
      )}

      {activeLayers.includes("pc") && selectedCommune && (
        <PCSLayer filters={layerFilters?.pc} commune={selectedCommune} />
      )}

      {activeLayers.includes("dpe") && (
        <DPELayer
          dpeFilter={layerFilters?.dpe?.["dpe-class"] as string[] | undefined}
          onMarkerClick={(dpe: IDPE) => {
            setSelectedPC(null)
            setSelectedDPE?.({
              dpe,
              longitude: Number(dpe.longitude),
              latitude: Number(dpe.latitude),
            })
          }}
        />
      )}

      {activeLayers.includes("plu") && (
        <CommunePluLayer
          pluTypeZones={pluTypeZones ?? ["U", "AUc", "AUs", "N", "A"]}
          pluLibelles={pluLibelles}
          opacity={pluOpacity ?? 0.5}
          pluData={pluData}
        />
      )}

      {(activeLayers.includes("abf") || activeLayers.includes("info-mh")) && (
        <ABFLayer
          opacity={layerFilters?.abf?.["abf-opacity"] != null ? (layerFilters.abf["abf-opacity"] as number) / 100 : 0.8}
        />
      )}

      {activeLayers.includes("info-gares") && selectedCommune && <StationPerimeterLayer commune={selectedCommune} />}

      {activeLayers.includes("qpv") && (
        <QPVLayer
          opacity={layerFilters?.qpv?.["qpv-opacity"] != null ? (layerFilters.qpv["qpv-opacity"] as number) / 100 : 0.8}
        />
      )}

      {activeLayers.includes("argile") && (
        <ArgileLayer
          opacity={
            layerFilters?.argile?.["argile-opacity"] != null
              ? (layerFilters.argile["argile-opacity"] as number) / 100
              : 0.7
          }
        />
      )}
      {activeLayers.includes("seisme") && (
        <EarthquakeLayer
          opacity={
            layerFilters?.seisme?.["seisme-opacity"] != null
              ? (layerFilters.seisme["seisme-opacity"] as number) / 100
              : 0.7
          }
        />
      )}
      {activeLayers.includes("radon") && (
        <RadonLayer
          opacity={
            layerFilters?.radon?.["radon-opacity"] != null ? (layerFilters.radon["radon-opacity"] as number) / 100 : 0.7
          }
        />
      )}
      {activeLayers.includes("inondation") && (
        <FloodingLayer
          opacity={
            layerFilters?.inondation?.["inond-opacity"] != null
              ? (layerFilters.inondation["inond-opacity"] as number) / 100
              : 0.7
          }
        />
      )}
      {activeLayers.includes("ppri") && (
        <PPRILayer
          opacity={
            layerFilters?.ppri?.["ppri-opacity"] != null ? (layerFilters.ppri["ppri-opacity"] as number) / 100 : 0.7
          }
        />
      )}
      {activeLayers.includes("pollution") && (
        <PollutionLayer
          opacity={
            layerFilters?.pollution?.["poll-opacity"] != null
              ? (layerFilters.pollution["poll-opacity"] as number) / 100
              : 0.7
          }
        />
      )}

      {activeLayers.includes("isochrone-walk") && isochroneCenter && (
        <IsochroneLayer
          profile="walking"
          center={isochroneCenter}
          opacity={
            layerFilters?.["isochrone-walk"]?.["iso-w-opacity"] != null
              ? (layerFilters["isochrone-walk"]["iso-w-opacity"] as number) / 100
              : 0.5
          }
        />
      )}
      {activeLayers.includes("isochrone-drive") && isochroneCenter && (
        <IsochroneLayer
          profile="driving"
          center={isochroneCenter}
          opacity={
            layerFilters?.["isochrone-drive"]?.["iso-d-opacity"] != null
              ? (layerFilters["isochrone-drive"]["iso-d-opacity"] as number) / 100
              : 0.5
          }
        />
      )}
      {activeLayers.includes("isochrone-bike") && isochroneCenter && (
        <IsochroneLayer
          profile="cycling"
          center={isochroneCenter}
          opacity={
            layerFilters?.["isochrone-bike"]?.["iso-b-opacity"] != null
              ? (layerFilters["isochrone-bike"]["iso-b-opacity"] as number) / 100
              : 0.5
          }
        />
      )}

      {activeLayers.includes("terrain-3d") && <ThreeDLayer />}
      {activeLayers.includes("denivele") && <TerrainLayer />}
      {activeLayers.includes("info-chaleur") && <HeatNetworkLayer commune={selectedCommune} />}

      {parcelPOI?.lon && parcelPOI?.lat && <Marker longitude={parcelPOI.lon} latitude={parcelPOI.lat} />}
      {poiMarkerElements}
      {mutationMarkerElements}

      {prescriptionPopup && (
        <Popup
          anchor="bottom"
          longitude={prescriptionPopup.longitude}
          latitude={prescriptionPopup.latitude}
          onClose={() => setPrescriptionPopup(null)}
          closeOnClick={false}
          className="rounded-md"
        >
          <div className="p-2 max-w-xs">
            <p className="font-semibold">{prescriptionPopup.libelle}</p>
            <p className="text-sm">{prescriptionPopup.txt}</p>
          </div>
        </Popup>
      )}

      {selectedPC && (
        <Popup
          anchor="bottom"
          longitude={selectedPC.longitude}
          latitude={selectedPC.latitude}
          onClose={() => setSelectedPC(null)}
          closeOnClick={false}
          className="rounded-md"
          maxWidth="480px"
        >
          <PCPopup permit={selectedPC.permit} />
        </Popup>
      )}

      {selectedDPE && (
        <Popup
          anchor="bottom"
          longitude={selectedDPE.longitude}
          latitude={selectedDPE.latitude}
          onClose={() => setSelectedDPE?.(null)}
          closeOnClick={false}
          className="rounded-md"
          maxWidth="480px"
        >
          <DPEPopup dpe={selectedDPE.dpe} />
        </Popup>
      )}

      {selectedProject && (
        <Popup
          anchor="bottom"
          longitude={selectedProject.longitude}
          latitude={selectedProject.latitude}
          onClose={() => setSelectedProject?.(null)}
          closeButton={false}
          closeOnClick={false}
          className="rounded-md"
          maxWidth="360px"
        >
          <ProjectPopup project={selectedProject.project} onClose={() => setSelectedProject?.(null)} />
        </Popup>
      )}

      <Source
        promoteId="id"
        id="cadastre"
        minzoom={11}
        maxzoom={16}
        type="vector"
        tiles={["https://openmaptiles.geo.data.gouv.fr/data/cadastre/{z}/{x}/{y}.pbf"]}
      >
        <Layer
          id="cadastre-layer"
          source="cadastre"
          source-layer="parcelles"
          type="fill"
          paint={{
            "fill-color": "#FFF",
            "fill-opacity": ["case", ["boolean", ["feature-state", "select"], false], 0.2, 0],
          }}
        />
        <Layer
          id="cadastre-layer-border"
          source="cadastre"
          source-layer="parcelles"
          type="line"
          paint={{ "line-color": "#FFF", "line-opacity": 0.3 }}
        />
      </Source>

      <SelectedLayer selectedParcels={selectedParcels} />

      {isParcelFilterOn && parcelFilter && (
        <SearchParcelsLayer filter={parcelFilter} onDataLoaded={onParcelFilterDataLoaded} commune={selectedCommune} />
      )}

      {activeTool === "distance" && <MeasureDistance />}
      {activeTool === "surface" && <MeasureSurface />}
      {activeTool === "altitude" && <Altimetry />}
    </MapGL>
  )
})
